/**
 * Máy chủ giả của module `bien-ban` (`/api/v1/m/bien-ban/sessions`, ADR-0126 mục 5). Màu và nhãn không ở đây
 * (`modules/bien-ban/constants.ts`): tầng này bị xoá khi có API thật.
 *
 * Tình huống mẫu để soát:
 * - `bb-001` xong, độ tin cậy cao, không có cảnh báo; `bb-002` độ tin cậy trung bình, có đoạn đã sửa;
 * - `bb-003` độ tin cậy thấp, tóm tắt lỗi, bản chép lời vẫn dùng được; `Tóm tắt lại` lần này qua;
 * - `bb-004` đang chép lời, `bb-005` đang xếp hàng: tự chuyển trạng thái sau vài chục giây kể từ lúc tải trang;
 * - `bb-006` lỗi nhận dạng, `Thử lại` chạy lại từ đầu; `bb-007` không còn tệp ghi âm (`404` ở `GET audio`);
 * - `bb-008` hết hạn mức token AI: câu của cổng AI cộng câu của module, `Thử lại` được khi có thêm hạn mức;
 * - `bb-002`: URL ký đầu tiên đã hết hạn, trình phát phải tự xin URL mới và phát tiếp đúng chỗ;
 * - tải tệp tên có `loi` thì phiên lỗi lúc chép lời; tệp tên có `hong` bị từ chối dưới ô tệp; tiêu đề `Lỗi tiêu đề` nhận `400` dưới ô tiêu đề.
 *
 * Âm thanh mẫu là tín hiệu tổng hợp 4 kHz dựng ngay ở trình duyệt, không có tệp nào trong kho.
 */
import type { ApiError } from '../lib/errors';
import type {
  AmThanhKy, DinhDangXuat, Doan, DoanSua, NguoiNoi, Phien, PhienSua, PhienTao, TomTat, TomTatGhi, TrangPhien, TrangThaiPhien,
} from '../modules/bien-ban/types';

const loi = (status: number, code: string, message: string, pointer?: string): ApiError => ({
  status, code, message, ...(pointer ? { details: [{ pointer, detail: message }] } : {}),
});
const khongThay = (): ApiError => loi(404, 'NOT_FOUND', 'Không tìm thấy phiên. Phiên có thể đã bị xoá.');

const KICH_BAN: ReadonlyArray<readonly [string, number, number, string]> = [
  ['SPEAKER_00', 1, 7, 'Chào mọi người, hôm nay mình rà lại tiến độ triển khai cổng tenant và chốt lịch tuần tới.'],
  ['SPEAKER_00', 7, 13, 'Trước hết nhờ chị Lan cập nhật phần hạ tầng, sau đó anh Minh nói về dữ liệu chuyển đổi.'],
  ['SPEAKER_01', 14, 22, 'Phần hạ tầng đã xong môi trường thử nghiệm, còn thiếu chứng chỉ cho tên miền của khách hàng.'],
  ['SPEAKER_01', 22, 29, 'Chứng chỉ dự kiến có vào thứ Tư. Nếu trễ thì lịch đào tạo phải lùi theo.'],
  ['SPEAKER_00', 30, 34, 'Vậy mình giữ thứ Tư là mốc cuối, trễ nữa thì báo ngay trong nhóm.'],
  ['SPEAKER_02', 35, 44, 'Dữ liệu chuyển đổi đã chạy thử hai lần, lần thứ hai còn khoảng ba trăm hồ sơ thiếu mã phòng ban.'],
  ['SPEAKER_02', 44, 52, 'Tôi đề nghị phía khách hàng rà lại danh mục phòng ban trước khi chạy lần cuối.'],
  ['SPEAKER_01', 53, 58, 'Bên khách hàng có người đầu mối chưa anh?'],
  ['SPEAKER_02', 58, 64, 'Có chị Hoa bên nhân sự, tôi sẽ gửi danh sách hồ sơ lỗi cho chị ấy trong hôm nay.'],
  ['SPEAKER_00', 65, 72, 'Thống nhất: chạy dữ liệu lần cuối vào thứ Sáu, sau khi chị Hoa xác nhận danh mục.'],
  ['SPEAKER_00', 72, 80, 'Lịch đào tạo giữ nguyên ngày mười hai, buổi sáng cho quản trị, buổi chiều cho người dùng.'],
  ['SPEAKER_01', 81, 87, 'Tôi sẽ chuẩn bị tài khoản đào tạo và gửi hướng dẫn trước một ngày.'],
  ['SPEAKER_00', 88, 95, 'Cảm ơn mọi người. Biên bản sẽ gửi sau cuộc họp, ai có góp ý thì phản hồi trong hôm nay.'],
];
const THOI_LUONG_MAU = 96;

const tomTatMau = (edited = false): TomTat => ({
  overview: 'Cuộc họp rà tiến độ triển khai cổng tenant: hạ tầng còn chờ chứng chỉ tên miền, dữ liệu chuyển đổi còn khoảng 300 hồ sơ thiếu mã phòng ban. Lịch đào tạo giữ nguyên.',
  key_points: [
    'Môi trường thử nghiệm đã xong, còn thiếu chứng chỉ cho tên miền của khách hàng.',
    'Lần chạy thử dữ liệu thứ hai còn khoảng 300 hồ sơ thiếu mã phòng ban.',
    'Khách hàng cần rà lại danh mục phòng ban trước lần chạy cuối.',
  ],
  decisions: ['Thứ Tư là mốc cuối cho chứng chỉ tên miền.', 'Chạy dữ liệu lần cuối vào thứ Sáu, sau khi khách hàng xác nhận danh mục.', 'Lịch đào tạo giữ nguyên ngày 12.'],
  action_items: [
    { task: 'Gửi danh sách hồ sơ lỗi cho chị Hoa', owner: 'Anh Minh', due: 'Hôm nay' },
    { task: 'Lấy chứng chỉ tên miền của khách hàng', owner: 'Chị Lan', due: 'Thứ Tư' },
    { task: 'Chuẩn bị tài khoản đào tạo và hướng dẫn', owner: 'Chị Lan', due: 'Trước ngày 12' },
    { task: 'Gửi biên bản cuộc họp', owner: null, due: null },
  ],
  edited,
});

interface BanGhi extends Phien {
  doan: Doan[];
  /** Mốc tự chuyển sang bước sau; `null` khi không còn chạy nền. */
  moc: { chepLoi: number; tomTat: number; xong: number } | null;
  mocTomTatLai: number | null;
  sapLoi: boolean;
  urlAmThanh: string | null;
  khongCoAmThanh: boolean;
  urlHetHan: boolean;
}

const doanMau = (idPhien: string, heSo = 1): Doan[] =>
  KICH_BAN.map(([speaker, start, end, text], i) => ({
    id: `${idPhien}-d${i + 1}`, index: i, start: start * heSo, end: end * heSo, speaker, text, edited: false,
  }));

const tinhNguoiNoi = (ds: readonly Doan[], cu: readonly NguoiNoi[]): NguoiNoi[] => {
  const giay = new Map<string, number>();
  for (const d of ds) if (d.speaker) giay.set(d.speaker, (giay.get(d.speaker) ?? 0) + d.end - d.start);
  return [...giay.keys()].sort().map((label) => ({
    label,
    name: cu.find((n) => n.label === label)?.name ?? null,
    speech_seconds: Math.round(giay.get(label)!),
  }));
};

const NGUOI = [['u-004', 'Phạm Thu Hà'], ['u-010', 'Nguyễn Văn Nam'], ['u-016', 'Lê Thị Mai']] as const;

let dem = 100;

function banGhi(id: string, tieuDe: string, ngay: string, them: Partial<BanGhi>): BanGhi {
  const doan = them.doan ?? doanMau(id);
  const [ma, ten] = NGUOI[Number(id.slice(-1)) % NGUOI.length]!;
  return {
    id,
    title: tieuDe,
    status: 'DONE',
    file_name: `${tieuDe.toLowerCase().replace(/\s+/g, '-')}.mp3`,
    file_size: 18_400_000,
    content_type: 'audio/mpeg',
    language: 'vi',
    duration_seconds: THOI_LUONG_MAU,
    num_speakers: null,
    reliability: 'high',
    reliability_note: null,
    error_code: null,
    error_message: null,
    summary_status: 'DONE',
    summary_error: null,
    summary: tomTatMau(),
    created_at: ngay,
    updated_at: ngay,
    created_by: ma,
    created_by_name: ten,
    moc: null,
    mocTomTatLai: null,
    sapLoi: false,
    urlAmThanh: null,
    khongCoAmThanh: false,
    urlHetHan: false,
    ...them,
    doan,
    speakers: tinhNguoiNoi(doan, them.speakers ?? []),
  };
}

const luc = Date.now();
const chuaXong = { doan: [], speakers: [], duration_seconds: null, reliability: null, summary: null, summary_status: 'NONE' as const };

const DS: BanGhi[] = [
  banGhi('bb-001', 'Họp giao ban triển khai cổng tenant', '2026-11-05T08:30:00+07:00', {
    speakers: [{ label: 'SPEAKER_00', name: 'Phạm Thu Hà', speech_seconds: 0 }, { label: 'SPEAKER_01', name: 'Chị Lan', speech_seconds: 0 }, { label: 'SPEAKER_02', name: 'Anh Minh', speech_seconds: 0 }],
  }),
  banGhi('bb-002', 'Phỏng vấn ứng viên kỹ sư dữ liệu', '2026-11-04T15:10:00+07:00', {
    reliability: 'medium',
    reliability_note: 'Có đoạn hai người nói chồng lên nhau, máy khó tách nhãn ở các đoạn đó.',
    urlHetHan: true,
    summary: tomTatMau(true),
  }),
  banGhi('bb-003', 'Trao đổi nhanh với khách hàng Minh Long qua điện thoại', '2026-11-04T10:00:00+07:00', {
    reliability: 'low',
    reliability_note: 'Âm thanh chỉ có một kênh và nhiều tiếng ồn, nhãn người nói ở nhiều đoạn có thể bị đảo.',
    summary: null,
    summary_status: 'FAILED',
    summary_error: 'Tổ chức đã hết hạn mức AI nên chưa tóm tắt được. Bản chép lời vẫn dùng được; hãy bấm "Tóm tắt lại" khi có thêm hạn mức.',
  }),
  banGhi('bb-004', 'Họp hội đồng quản trị quý 4', '2026-11-05T09:40:00+07:00', {
    ...chuaXong, status: 'TRANSCRIBING', file_size: 240_000_000, moc: { chepLoi: luc, tomTat: luc + 40_000, xong: luc + 55_000 },
  }),
  banGhi('bb-005', 'Đào tạo sử dụng phần mềm cho chi nhánh Đà Nẵng', '2026-11-05T09:45:00+07:00', {
    ...chuaXong, status: 'QUEUED', moc: { chepLoi: luc + 60_000, tomTat: luc + 100_000, xong: luc + 115_000 },
  }),
  banGhi('bb-006', 'Họp phòng kinh doanh tháng 10', '2026-11-03T14:00:00+07:00', {
    ...chuaXong,
    status: 'FAILED',
    error_code: 'AI_UNAVAILABLE',
    error_message: 'Máy chủ nhận dạng giọng nói đang không phản hồi. Thử lại sau ít phút.',
  }),
  banGhi('bb-007', 'Họp cũ đã dọn tệp ghi âm', '2026-10-30T09:00:00+07:00', { khongCoAmThanh: true }),
  banGhi('bb-008', 'Họp tổng kết dự án tháng 10', '2026-11-05T07:50:00+07:00', {
    ...chuaXong,
    status: 'FAILED',
    error_code: 'AI_QUOTA_EXCEEDED',
    error_message: 'Tổ chức đã hết hạn mức token AI tháng này (đã dùng 1.000.000 / 1.000.000 token). Các chức năng dùng AI tạm dừng tới 00:00 ngày 01/12/2026; các chức năng khác vẫn dùng bình thường. Liên hệ quản trị nền tảng nếu cần nâng hạn mức. Khi có thêm hạn mức, hãy bấm Thử lại.',
  }),
  banGhi('bb-009', 'Hội thảo khách hàng cả ngày', '2026-11-05T07:40:00+07:00', {
    ...chuaXong,
    status: 'FAILED',
    error_code: 'AI_QUOTA_INSUFFICIENT',
    error_message: 'Bản ghi này cần 46.080 token, hạn mức AI tổ chức đặt cho phòng ban Phòng Kinh doanh miền Trung tuần này chỉ còn 1.200 token. Cắt ngắn bản ghi hoặc liên hệ quản trị viên của tổ chức.',
  }),
  banGhi('bb-010', 'Phỏng vấn nhà cung cấp', '2026-11-05T07:30:00+07:00', {
    ...chuaXong,
    status: 'FAILED',
    error_code: 'AI_LIMIT_EXCEEDED',
    error_message: 'Đã dùng hết hạn mức AI tổ chức đặt cho bạn hôm nay (đã dùng 20.000 / 20.000 token). Hạn mức mở lại lúc 00:00 ngày 06/11/2026. Cần thêm thì liên hệ quản trị viên của tổ chức.',
  }),
  banGhi('bb-011', 'Họp phòng kế toán', '2026-11-05T07:20:00+07:00', {
    ...chuaXong,
    status: 'FAILED',
    error_code: 'AI_LIMIT_EXHAUSTED',
    error_message: 'Đã dùng hết hạn mức AI tổ chức đặt cho phòng ban Phòng Kế toán với tính năng bien-ban.nhan-dang tuần này nên lượt xử lý bị dừng giữa chừng. Hạn mức mở lại lúc 00:00 ngày 09/11/2026. Cần thêm thì liên hệ quản trị viên của tổ chức.',
  }),
  ...Array.from({ length: 16 }, (_x, i) =>
    banGhi(`bb-1${String(i).padStart(2, '0')}`, `Họp giao ban tuần ${44 - i}`, `2026-10-${String(28 - i).padStart(2, '0')}T08:30:00+07:00`, {}),
  ),
];

const VA_CHAM_TEN = /hong/i;

/** Đưa phiên đến đúng bước theo đồng hồ: máy chủ thật làm việc này ở worker. */
function cap(b: BanGhi): BanGhi {
  const bay = Date.now();
  if (b.moc) {
    if (bay >= b.moc.tomTat && b.sapLoi) {
      b.status = 'FAILED';
      b.error_code = 'WHISPER_FAILED';
      b.error_message = 'Không đọc được âm thanh của tệp. Kiểm tra tệp nghe được rồi thử lại.';
      b.moc = null;
      b.sapLoi = false;
      return b;
    }
    if (bay >= b.moc.chepLoi && b.status === 'QUEUED') b.status = 'TRANSCRIBING';
    if (bay >= b.moc.tomTat && b.status === 'TRANSCRIBING') {
      b.status = 'SUMMARIZING';
      const he = (b.duration_seconds ?? THOI_LUONG_MAU) / THOI_LUONG_MAU;
      b.doan = doanMau(b.id, he);
      b.speakers = tinhNguoiNoi(b.doan, b.speakers);
      b.duration_seconds = b.duration_seconds ?? THOI_LUONG_MAU;
      b.reliability = 'high';
      b.summary_status = 'PENDING';
    }
    if (bay >= b.moc.xong) {
      b.status = 'DONE';
      b.summary = tomTatMau();
      b.summary_status = 'DONE';
      b.moc = null;
    }
  }
  if (b.mocTomTatLai !== null && bay >= b.mocTomTatLai) {
    b.summary = tomTatMau();
    b.summary_status = 'DONE';
    b.summary_error = null;
    b.mocTomTatLai = null;
  }
  return b;
}

const ra = (b: BanGhi): Phien => {
  const { doan: _doan, moc: _moc, mocTomTatLai: _tl, sapLoi: _sl, urlAmThanh: _u, khongCoAmThanh: _k, urlHetHan: _h, ...phien } = b;
  return structuredClone(phien);
};

function tim(id: string): BanGhi {
  const b = DS.find((x) => x.id === id);
  if (!b) throw khongThay();
  return cap(b);
}

/** WAV PCM 8 bit, 4 kHz: mỗi người nói một cao độ, khoảng lặng giữa các đoạn. Khoảng 400 KB cho 96 giây. */
function taoWav(ds: readonly Doan[], giay: number): Blob {
  const tanSo = 4000;
  const soMau = Math.ceil(giay * tanSo);
  const buf = new ArrayBuffer(44 + soMau);
  const v = new DataView(buf);
  const ghi = (o: number, s: string) => [...s].forEach((c, i) => v.setUint8(o + i, c.charCodeAt(0)));
  ghi(0, 'RIFF');
  v.setUint32(4, 36 + soMau, true);
  ghi(8, 'WAVEfmt ');
  v.setUint32(16, 16, true);
  v.setUint16(20, 1, true);
  v.setUint16(22, 1, true);
  v.setUint32(24, tanSo, true);
  v.setUint32(28, tanSo, true);
  v.setUint16(32, 1, true);
  v.setUint16(34, 8, true);
  ghi(36, 'data');
  v.setUint32(40, soMau, true);
  new Uint8Array(buf, 44).fill(128);
  for (const d of ds) {
    const cao = 180 + 70 * (d.speaker ? Number(d.speaker.slice(-1)) : 0);
    for (let i = Math.floor(d.start * tanSo); i < Math.min(soMau, d.end * tanSo); i++) {
      v.setUint8(44 + i, 128 + Math.round(18 * Math.sin((2 * Math.PI * cao * i) / tanSo)));
    }
  }
  return new Blob([buf], { type: 'audio/wav' });
}

/** Đọc thời lượng thật của tệp vừa chọn để mốc trong bản chép lời khớp tệp; không đọc được thì dùng độ dài mẫu. */
function docThoiLuong(url: string): Promise<number> {
  return new Promise((xong) => {
    const a = new Audio();
    const hen = setTimeout(() => xong(THOI_LUONG_MAU), 1500);
    a.onloadedmetadata = () => {
      clearTimeout(hen);
      xong(Number.isFinite(a.duration) && a.duration > 0 ? Math.ceil(a.duration) : THOI_LUONG_MAU);
    };
    a.onerror = () => {
      clearTimeout(hen);
      xong(THOI_LUONG_MAU);
    };
    a.src = url;
  });
}

export const mayChuBienBan = {
  danhSach({ search, status, limit, cursor }: { search?: string; status?: TrangThaiPhien; limit: number; cursor?: string }): TrangPhien {
    const tu = search?.trim().toLowerCase();
    const loc = [...DS]
      .map(cap)
      .filter((b) => (!status || b.status === status) && (!tu || b.title.toLowerCase().includes(tu) || b.file_name.toLowerCase().includes(tu)))
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
    const dau = cursor ? Number(cursor) : 0;
    const cuoi = dau + limit;
    return { items: loc.slice(dau, cuoi).map(ra), next_cursor: cuoi < loc.length ? String(cuoi) : null };
  },

  chiTiet: (id: string): Phien => ra(tim(id)),

  async tao(vao: PhienTao, tuyChon: { onTienTrinh?: (phanTram: number) => void; signal?: AbortSignal }): Promise<Phien> {
    for (let pt = 0; pt <= 100; pt += 10) {
      if (tuyChon.signal?.aborted) throw { code: 'UPLOAD_CANCELLED', message: '' } satisfies ApiError;
      tuyChon.onTienTrinh?.(pt);
      await new Promise((xong) => setTimeout(xong, 120));
    }
    if (VA_CHAM_TEN.test(vao.file.name)) throw loi(400, 'BIEN_BAN_FILE_TYPE_NOT_SUPPORTED', 'Nội dung tệp không khớp đuôi tệp. Hãy kiểm tra tệp nghe được rồi tải lại.');
    if (vao.title.trim() === 'Lỗi tiêu đề') throw loi(400, 'INVALID_ARGUMENT', 'Tiêu đề không hợp lệ.', '/title');
    const urlAmThanh = URL.createObjectURL(vao.file);
    const giay = await docThoiLuong(urlAmThanh);
    const bay = Date.now();
    const b = banGhi(`bb-${++dem}0`, vao.title, new Date(bay).toISOString(), {
      ...chuaXong,
      status: 'QUEUED',
      file_name: vao.file.name,
      file_size: vao.file.size,
      content_type: vao.file.type,
      language: vao.language === 'auto' ? null : vao.language,
      num_speakers: vao.num_speakers ?? null,
      duration_seconds: giay,
      sapLoi: /loi/i.test(vao.file.name),
      urlAmThanh,
      created_by: 'u-004',
      created_by_name: 'Phạm Thu Hà',
      moc: { chepLoi: bay + 3000, tomTat: bay + 8000, xong: bay + 12000 },
    });
    DS.unshift(b);
    return ra(b);
  },

  sua(id: string, vao: PhienSua): Phien {
    const b = tim(id);
    if (vao.title !== undefined) {
      if (!vao.title.trim()) throw loi(400, 'INVALID_ARGUMENT', 'Tiêu đề không được để trống.', '/title');
      b.title = vao.title.trim();
    }
    if (vao.speakers) {
      b.speakers = b.speakers.map((n) => (n.label in vao.speakers! ? { ...n, name: vao.speakers![n.label]?.trim() || null } : n));
    }
    b.updated_at = new Date().toISOString();
    return ra(b);
  },

  xoa(id: string): void {
    const i = DS.findIndex((x) => x.id === id);
    if (i < 0) throw khongThay();
    const [b] = DS.splice(i, 1);
    if (b?.urlAmThanh) URL.revokeObjectURL(b.urlAmThanh);
  },

  doan: (id: string): Doan[] => structuredClone(tim(id).doan),

  suaDoan(id: string, doanId: string, vao: DoanSua): Doan {
    const b = tim(id);
    const d = b.doan.find((x) => x.id === doanId);
    if (!d) throw khongThay();
    if (vao.text !== undefined) {
      if (!vao.text.trim()) throw loi(400, 'INVALID_ARGUMENT', 'Nội dung đoạn không được để trống.', '/text');
      d.text = vao.text.trim();
    }
    if (vao.speaker !== undefined) d.speaker = vao.speaker;
    d.edited = true;
    b.speakers = tinhNguoiNoi(b.doan, b.speakers);
    return structuredClone(d);
  },

  amThanh(id: string): AmThanhKy {
    const b = tim(id);
    if (b.khongCoAmThanh) throw loi(404, 'NOT_FOUND', 'Tệp ghi âm của phiên này không còn trên máy chủ.');
    if (b.urlHetHan) {
      // URL ký đã hết hạn: trình phát phải tự xin lại, người dùng không thấy gì.
      b.urlHetHan = false;
      const cu = URL.createObjectURL(new Blob());
      URL.revokeObjectURL(cu);
      return { url: cu, expires_in: 0 };
    }
    b.urlAmThanh ??= URL.createObjectURL(taoWav(b.doan.length ? b.doan : doanMau(b.id), b.duration_seconds ?? THOI_LUONG_MAU));
    return { url: b.urlAmThanh, expires_in: 900 };
  },

  thuLai(id: string): void {
    const b = tim(id);
    if (b.status !== 'FAILED') throw loi(409, 'BIEN_BAN_SESSION_STATE', 'Phiên không ở trạng thái lỗi.');
    const bay = Date.now();
    Object.assign(b, { status: 'QUEUED' as TrangThaiPhien, error_code: null, error_message: null, moc: { chepLoi: bay + 2000, tomTat: bay + 7000, xong: bay + 11000 } });
  },

  tomTatLai(id: string): void {
    const b = tim(id);
    if (!b.doan.length) throw loi(409, 'BIEN_BAN_NO_TRANSCRIPT', 'Phiên chưa có bản chép lời để tóm tắt.');
    b.summary_status = 'PENDING';
    b.summary_error = null;
    b.mocTomTatLai = Date.now() + 5000;
  },

  suaTomTat(id: string, vao: TomTatGhi): void {
    const b = tim(id);
    if (!vao.overview.trim()) throw loi(400, 'INVALID_ARGUMENT', 'Tổng quan không được để trống.', '/overview');
    b.summary = { ...structuredClone(vao), edited: true };
    b.summary_status = 'DONE';
    b.summary_error = null;
  },

  /** Tệp mẫu đều là chữ thuần, kể cả khi xuất Word: máy chủ giả không dựng `.docx` thật. */
  xuat(id: string, dinhDang: DinhDangXuat, tenMacDinh: string): { blob: Blob; ten: string } {
    const b = tim(id);
    const ten = (l: string | null) => b.speakers.find((n) => n.label === l)?.name ?? l ?? '';
    const chu = [b.title, '', ...b.doan.map((d) => `${ten(d.speaker)}: ${d.text}`)].join('\n');
    return { blob: new Blob([chu], { type: dinhDang === 'docx' ? 'application/octet-stream' : 'text/plain' }), ten: tenMacDinh };
  },
};
